import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../../context/SupabaseContext';
import {
  resolveGrowthAccess,
  resolveGrowthLegacyRoute,
  type GrowthActivityKind,
} from '../../services/growthService';
import { LoadingState } from '../common/LoadingState';

export function useGrowthMigrationEnabled() {
  const { salonId } = useSupabase();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      const access = await resolveGrowthAccess(salonId);
      if (active) {
        setEnabled(access.canViewGrowth && access.showGrowthInNavigation);
      }
    };

    void resolve();
    return () => {
      active = false;
    };
  }, [salonId]);

  return { enabled, salonId };
}

export function GrowthCompatibilityRoute({
  children,
  growthPath,
  preserveWhenSearch = false,
}: {
  children: ReactNode;
  growthPath: string;
  preserveWhenSearch?: boolean;
}) {
  const location = useLocation();
  const { enabled } = useGrowthMigrationEnabled();

  if (enabled === null) {
    return <LoadingState className="min-h-[40vh]" />;
  }

  if (enabled && !(preserveWhenSearch && location.search)) {
    return <Navigate to={`${growthPath}${location.search}`} replace />;
  }

  return <>{children}</>;
}

export function GrowthLegacyResourceRoute({
  children,
  resourceId,
  resourceKind,
}: {
  children: ReactNode;
  resourceId?: string;
  resourceKind: GrowthActivityKind;
}) {
  const location = useLocation();
  const { enabled, salonId } = useGrowthMigrationEnabled();
  const [targetPath, setTargetPath] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      if (!enabled || !salonId || !resourceId) {
        if (active) setTargetPath(null);
        return;
      }

      try {
        const resolution = await resolveGrowthLegacyRoute(salonId, resourceKind, resourceId);
        if (active) setTargetPath(resolution.target_path);
      } catch (error) {
        console.error('[GrowthLegacyResourceRoute] failed to resolve retained lineage', error);
        if (active) setTargetPath(null);
      }
    };

    if (enabled !== null) void resolve();
    return () => {
      active = false;
    };
  }, [enabled, resourceId, resourceKind, salonId]);

  if (enabled === null || targetPath === undefined) {
    return <LoadingState className="min-h-[40vh]" />;
  }

  if (targetPath) {
    return <Navigate to={`${targetPath}${location.search}`} replace />;
  }

  return <>{children}</>;
}